import { useEffect, useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Flex, Text, Heading, Box,
  Input, FormLabel, Checkbox, IconButton, FormControl,
  Spinner,
} from "@chakra-ui/react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  FaAngleRight, FaAngleLeft, FaAngleDoubleRight, FaAngleDoubleLeft,
  FaEye, FaFileExcel, FaTimes,
} from "react-icons/fa";
import { crm } from "../../../theme";
import ReportPreviewModal from "./reportPreviewModal";
import {
  TEXT_FILTER_FIELDS, DATE_FILTER_FIELDS, COLUMN_FILTERS, FILTER_KEY_TO_COLUMN,
} from "./filterFields";
import MultipleSelect from "../../../components/MultipleSelect";
import SearchableSelect from "../../../components/SearchableSelect";
import { processStatus, CompanyFormData } from "../../Configurations/tabs/types";
import { getCompanies } from "../../../services/companyService";
import { getUsers } from "../../../services/userService";
import { getResponsibleOptions, ResponsibleOption } from "../../../utils/users";

export { TEXT_FILTER_FIELDS, DATE_FILTER_FIELDS };

// ── Types ─────────────────────────────────────────────────────────

export interface DateRangeFilter {
  from: string;
  to: string;
}

export interface ReportFilters {
  text: Record<string, string>;
  dates: Record<string, DateRangeFilter>;
  clientLabels?: string;
}

export const ALL_FIELDS: string[] = [
  "CVF Ref", "Número Ref.", "Responsável", "Cliente", "Aparece pro cliente",
  "Despachante", "Status", "Incoterm", "Modo de Importação", "Exportador",
  "Total Invoice", "Data Abertura", "Data Embarque", "Data Chegada",
  "Frete internacional", "CE Mercante", "Forma de pagamento",
  "Número da invoice", "LI", "BL/AWB", "Nº Container", "Qtde. de Containers",
  "Embarcação", "Canal", "DI", "Data Registro", "Data do Desembaraço",
  "Agente de Cargas", "Local de Origem", "Local de Destino", "Armador",
  "Demurrage", "Data Emissão da NF", "NCM", "Situação dos Câmbioss",
  "Moeda invoice", "Free Time",
  "Cotação Internacional", "Cotação Rodoviária", "Seguro", "ICMS",
  "Numerário", "Documentos", "Catálogo PU", "Status LI", "Segmento",
  "Taxa Câmbio 1", "Data Câmbio 1", "Valor Parcela 1", "Contrato 1º Pagamento",
  "Data Pagamento 1", "Taxa Câmbio 2", "Data Câmbio 2", "Valor Parcela 2",
  "Data Pagamento 2", "Contrato 2º pagamento", "Taxa Câmbio 3",
  "Data Câmbio 3", "Valor Parcela 3", "Data Pagamento 3",
];

const CANAL_OPTIONS = ["Verde", "Amarelo", "Vermelho", "Cinza"];

const sortPt = (a: string, b: string) => a.localeCompare(b, "pt-BR");

export const emptyFilters = (): ReportFilters => ({
  text: Object.fromEntries(TEXT_FILTER_FIELDS.map((f) => [f.key, ""])),
  dates: Object.fromEntries(
    DATE_FILTER_FIELDS.map((f) => [f.key, { from: "", to: "" }])
  ),
});

// Auto-seleciona colunas cujo filtro tem valor mas a coluna não está em `fields`
// (ex.: relatório antigo salvo com filtro numa coluna que foi removida da lista).
const getInitialSelected = (fields?: string[], filters?: ReportFilters): string[] => {
  const selected = [...(fields ?? [])];
  const f = filters ?? emptyFilters();

  const tryAdd = (key: string) => {
    const col = FILTER_KEY_TO_COLUMN[key];
    if (col && !selected.includes(col)) selected.push(col);
  };

  Object.entries(f.text).forEach(([k, v]) => { if (v) tryAdd(k); });
  Object.entries(f.dates).forEach(([k, v]) => { if (v.from || v.to) tryAdd(k); });

  return selected;
};

// Limpa o valor do filtro associado a uma coluna (usado quando a coluna sai do relatório)
const clearFieldFilter = (field: string, filters: ReportFilters): ReportFilters => {
  const cf = COLUMN_FILTERS[field];
  if (!cf) return filters;

  if (cf.kind === "date") {
    return { ...filters, dates: { ...filters.dates, [cf.key]: { from: "", to: "" } } };
  }

  const next: ReportFilters = { ...filters, text: { ...filters.text, [cf.key]: "" } };
  if (cf.key === "Client") next.clientLabels = "";
  return next;
};

// ── Sub-components ────────────────────────────────────────────────

const MultiTagInput = ({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) => {
  const [input, setInput] = useState("");
  const values = value ? value.split("|").filter(Boolean) : [];

  const addValue = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed].join("|"));
      setInput("");
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Input
        size="xs"
        backgroundColor="white"
        borderRadius="6px"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") { e.preventDefault(); addValue(); }
        }}
        placeholder={placeholder ?? "Digite e pressione Enter"}
      />
      {values.length > 0 && (
        <Flex flexWrap="wrap" gap="6px" mt="6px">
          {values.map((v) => (
            <Flex
              key={v}
              backgroundColor="var(--icon-color)"
              borderColor="#5A96E3"
              borderRadius="3px"
              borderWidth="1px"
              height="24px"
              padding="2px 6px"
              color="white"
              alignItems="center"
              gap="6px"
              cursor="pointer"
              onClick={() => onChange(values.filter((x) => x !== v).join("|"))}
            >
              <Text fontWeight="semibold" fontSize="xs">{v}</Text>
              <FaTimes size={9} />
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
};

const DateRangeInline = ({ filter, onChange }: {
  filter: DateRangeFilter;
  onChange: (f: DateRangeFilter) => void;
}) => (
  <Flex gap="6px" alignItems="center" onClick={(e) => e.stopPropagation()}>
    <Input
      size="xs" type="date" backgroundColor="white" borderRadius="6px"
      value={filter.from}
      onChange={(e) => onChange({ ...filter, from: e.target.value })}
    />
    <Text fontSize="xs" color={crm.textFaint} flexShrink={0}>até</Text>
    <Input
      size="xs" type="date" backgroundColor="white" borderRadius="6px"
      value={filter.to}
      onChange={(e) => onChange({ ...filter, to: e.target.value })}
    />
  </Flex>
);

// Controle de filtro renderizado ao lado do label da coluna selecionada
const InlineFilterControl = ({
  field, filters, setFilters, companies, loadingCompanies, responsibles, loadingResponsibles,
}: {
  field: string;
  filters: ReportFilters;
  setFilters: (f: ReportFilters) => void;
  companies: CompanyFormData[];
  loadingCompanies: boolean;
  responsibles: ResponsibleOption[];
  loadingResponsibles: boolean;
}) => {
  const cf = COLUMN_FILTERS[field];
  if (!cf) return null;

  const setText = (key: string, v: string) =>
    setFilters({ ...filters, text: { ...filters.text, [key]: v } });

  const splitPipe = (key: string) =>
    filters.text[key] ? filters.text[key].split("|").filter(Boolean) : [];

  if (cf.kind === "date") {
    return (
      <DateRangeInline
        filter={filters.dates[cf.key] ?? { from: "", to: "" }}
        onChange={(v) => setFilters({ ...filters, dates: { ...filters.dates, [cf.key]: v } })}
      />
    );
  }

  if (cf.kind === "status") {
    return (
      <Box onClick={(e) => e.stopPropagation()} flex={1}>
        <MultipleSelect
          selectedValues={splitPipe(cf.key)}
          onChange={(vals) => setText(cf.key, vals.join("|"))}
          options={processStatus.map((p) => p.value)}
          placeholder="Selecione os status..."
          backgroundColor="white"
        />
      </Box>
    );
  }

  if (cf.kind === "channel") {
    return (
      <Box onClick={(e) => e.stopPropagation()} flex={1}>
        <MultipleSelect
          selectedValues={splitPipe(cf.key)}
          onChange={(vals) => setText(cf.key, vals.join("|"))}
          options={CANAL_OPTIONS}
          placeholder="Selecione os canais..."
          backgroundColor="white"
        />
      </Box>
    );
  }

  if (cf.kind === "responsible") {
    // Autocomplete multi de usuários (não-clientes, empresa do logado) + texto livre
    // (isCreatable), p/ responsáveis antigos importados do Excel que não são usuários.
    // Guarda os NOMES pipe-delimited — backend casa com ResponsibleUser ou com o campo legado.
    const options = responsibles.map((u) => ({ value: u.name, label: u.name }));
    const defaultValues = splitPipe(cf.key).map((n) => ({ value: n, label: n }));

    if (loadingResponsibles) {
      return <Flex justifyContent="center" flex={1}><Spinner size="sm" /></Flex>;
    }

    return (
      <Box onClick={(e) => e.stopPropagation()} flex={1}>
        <SearchableSelect
          isMulti
          width="100%"
          options={options}
          defaultMultiValues={defaultValues}
          onMultiChange={(vals) => setText(cf.key, vals.map((v) => v.value).join("|"))}
          isLoading={loadingResponsibles}
          menuPortal
          isCreatable
          placeholder="Selecione ou digite..."
        />
      </Box>
    );
  }

  if (cf.kind === "client") {
    const clientOptions = companies.map((c) => ({ value: c.id, label: c.name, subLabel: c.cnpj }));
    const defaultClientValues = splitPipe("Client")
      .map((id) => companies.find((c) => c.id === id))
      .filter(Boolean)
      .map((c) => ({ value: c!.id, label: c!.name, subLabel: c!.cnpj }));

    if (loadingCompanies) {
      return <Flex justifyContent="center" flex={1}><Spinner size="sm" /></Flex>;
    }

    return (
      <Box onClick={(e) => e.stopPropagation()} flex={1}>
        <SearchableSelect
          isMulti
          width="100%"
          options={clientOptions}
          defaultMultiValues={defaultClientValues}
          onMultiChange={(vals) => setFilters({
            ...filters,
            text: { ...filters.text, Client: vals.map((v) => v.value).join("|") },
            clientLabels: vals.map((v) => v.label).join("|"),
          })}
          isLoading={loadingCompanies}
          menuPortal
          placeholder="Selecione os clientes..."
        />
      </Box>
    );
  }

  // text
  return (
    <MultiTagInput
      value={filters.text[cf.key] ?? ""}
      onChange={(v) => setText(cf.key, v)}
      placeholder="Digite e Enter p/ adicionar"
    />
  );
};

// ── Field lists ───────────────────────────────────────────────────

const AvailableFieldList = ({ fields, checked, onToggle }: {
  fields: string[]; checked: string[]; onToggle: (field: string) => void;
}) => (
  <Box overflowY="auto" maxHeight="48vh" px="8px" py="8px">
    {fields.length === 0 ? (
      <Flex justifyContent="center" alignItems="center" py="32px">
        <Text fontSize="sm" color="gray.300">Todos selecionados</Text>
      </Flex>
    ) : fields.map((field) => (
      <Flex key={field} px="12px" py="8px" borderRadius="6px" alignItems="center"
        gap="10px" cursor="pointer"
        _hover={{ backgroundColor: "blue.50" }}
        backgroundColor={checked.includes(field) ? "blue.50" : "white"}
        onClick={() => onToggle(field)}>
        <Checkbox isChecked={checked.includes(field)}
          onChange={() => onToggle(field)}
          onClick={(e) => e.stopPropagation()} colorScheme="blue" />
        <Text fontSize="sm" color="gray.700" flex={1}>{field}</Text>
      </Flex>
    ))}
  </Box>
);

const SelectedFieldList = ({
  fields, checked, onToggle, filters, setFilters, companies, loadingCompanies,
  responsibles, loadingResponsibles,
}: {
  fields: string[]; checked: string[]; onToggle: (field: string) => void;
  filters: ReportFilters; setFilters: (f: ReportFilters) => void;
  companies: CompanyFormData[]; loadingCompanies: boolean;
  responsibles: ResponsibleOption[]; loadingResponsibles: boolean;
}) => (
  <Box overflowY="auto" maxHeight="48vh" px="8px" py="8px">
    {fields.length === 0 ? (
      <Flex justifyContent="center" alignItems="center" py="32px">
        <Text fontSize="sm" color="gray.300">Nenhuma coluna adicionada</Text>
      </Flex>
    ) : fields.map((field) => (
      <Flex key={field} px="12px" py="8px" borderRadius="6px" alignItems="center"
        gap="10px" cursor="pointer"
        _hover={{ backgroundColor: "green.50" }}
        backgroundColor={checked.includes(field) ? "green.50" : "white"}
        onClick={() => onToggle(field)}>
        <Checkbox isChecked={checked.includes(field)}
          onChange={() => onToggle(field)}
          onClick={(e) => e.stopPropagation()} colorScheme="green" flexShrink={0} />
        <Text fontSize="sm" color="gray.700" w="150px" flexShrink={0}>{field}</Text>
        <Box flex={1}>
          <InlineFilterControl
            field={field} filters={filters} setFilters={setFilters}
            companies={companies} loadingCompanies={loadingCompanies}
            responsibles={responsibles} loadingResponsibles={loadingResponsibles}
          />
        </Box>
      </Flex>
    ))}
  </Box>
);

// ── Main Modal ────────────────────────────────────────────────────

interface NewReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (name: string, fields: string[], filters: ReportFilters) => void;
  getProcesses: (params: any) => Promise<any>;
  currentCompany: any;
  currentEmulation: any;
  // Para edição — pré-preenche os campos
  initialName?: string;
  initialFields?: string[];
  initialFilters?: ReportFilters;
}

const NewReportModal = ({
  isOpen, onClose, onSave, getProcesses, currentCompany, currentEmulation,
  initialName, initialFields, initialFilters,
}: NewReportModalProps) => {
  const auth = useAuthUser<any>();
  const [name, setName] = useState(initialName ?? "");
  const [filters, setFilters] = useState<ReportFilters>(initialFilters ?? emptyFilters());
  const [selected, setSelected] = useState<string[]>(
    getInitialSelected(initialFields, initialFilters)
  );
  const [available, setAvailable] = useState<string[]>(
    ALL_FIELDS.filter((f) => !getInitialSelected(initialFields, initialFilters).includes(f))
  );
  const [companies, setCompanies] = useState<CompanyFormData[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [responsibles, setResponsibles] = useState<ResponsibleOption[]>([]);
  const [loadingResponsibles, setLoadingResponsibles] = useState(true);
  const [checkedAvailable, setCheckedAvailable] = useState<string[]>([]);
  const [checkedSelected, setCheckedSelected] = useState<string[]>([]);
  const [searchAvail, setSearchAvail] = useState("");
  const [searchSelected, setSearchSelected] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const initSelected = getInitialSelected(initialFields, initialFilters);
      setName(initialName ?? "");
      setFilters(initialFilters ?? emptyFilters());
      setSelected(initSelected);
      setAvailable(ALL_FIELDS.filter((f) => !initSelected.includes(f)));
      setCheckedAvailable([]);
      setCheckedSelected([]);
      setSearchAvail("");
      setSearchSelected("");
    }
  }, [isOpen, initialName, initialFields, initialFilters]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      const response = await getCompanies(false, currentCompany);
      if (response) {
        const filtered = (response as CompanyFormData[]).filter(
          (c: CompanyFormData) =>
            auth?.admin ||
            !auth?.clients?.length ||
            auth.clients.some((cc: any) => cc.companyId === c.id)
        );
        setCompanies(filtered);
      }
      setLoadingCompanies(false);
    };
    fetchCompanies();
  }, [isOpen, currentCompany]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchResponsibles = async () => {
      setLoadingResponsibles(true);
      const data = await getUsers();
      setResponsibles(getResponsibleOptions(data, auth?.company));
      setLoadingResponsibles(false);
    };
    fetchResponsibles();
  }, [isOpen, auth?.company]);

  const handleClose = () => {
    const initSelected = getInitialSelected(initialFields, initialFilters);
    setName(initialName ?? "");
    setFilters(initialFilters ?? emptyFilters());
    setSelected(initSelected);
    setAvailable(ALL_FIELDS.filter((f) => !initSelected.includes(f)));
    onClose();
  };

  const handleSave = () => {
    onSave?.(name, selected, filters);
    handleClose();
  };

  const toggleAvail = (field: string) =>
    setCheckedAvailable((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  const toggleSel = (field: string) =>
    setCheckedSelected((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );

  const moveToSelected = () => {
    setSelected([...selected, ...checkedAvailable]);
    setAvailable(available.filter((f) => !checkedAvailable.includes(f)));
    setCheckedAvailable([]);
  };
  const moveToAvailable = () => {
    let nextFilters = filters;
    checkedSelected.forEach((f) => { nextFilters = clearFieldFilter(f, nextFilters); });
    setFilters(nextFilters);
    setAvailable([...available, ...checkedSelected]);
    setSelected(selected.filter((f) => !checkedSelected.includes(f)));
    setCheckedSelected([]);
  };
  const moveAllToSelected = () => {
    setSelected([...selected, ...[...available].sort(sortPt)]);
    setAvailable([]);
    setCheckedAvailable([]);
  };
  const moveAllToAvailable = () => {
    let nextFilters = filters;
    selected.forEach((f) => { nextFilters = clearFieldFilter(f, nextFilters); });
    setFilters(nextFilters);
    setAvailable([...available, ...selected]);
    setSelected([]);
    setCheckedSelected([]);
  };

  const filteredAvailable = useMemo(
    () => available
      .filter((f) => f.toLowerCase().includes(searchAvail.toLowerCase()))
      .sort(sortPt),
    [available, searchAvail]
  );
  const filteredSelected = useMemo(
    () => selected
      .filter((f) => f.toLowerCase().includes(searchSelected.toLowerCase()))
      .sort(sortPt),
    [selected, searchSelected]
  );

  const activeFilterCount =
    Object.values(filters.text).filter((v) => v.length > 0).length +
    Object.values(filters.dates).filter((v) => v.from.length > 0 || v.to.length > 0).length;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)" maxH="90vh">
          <ModalHeader borderBottomWidth="1px" borderColor="gray.100" pb="16px">
            <Heading size="md" fontWeight="normal" mb="16px">
              {initialName ? "Editar Relatório" : "Novo Relatório"}
            </Heading>
            <FormControl>
              <FormLabel fontSize="sm" color={crm.textMuted}>Nome do relatório</FormLabel>
              <Input
                backgroundColor="white"
                borderRadius="6px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Follow-up Março, Processos Encerrados..."
                autoFocus
              />
            </FormControl>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py="24px">
            <Flex gap="12px" alignItems="stretch">
              <Flex flex={4} flexDir="column" backgroundColor={crm.surface} borderRadius="8px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} overflow="hidden">
                <Flex px="16px" py="12px" borderBottomWidth="1px" borderColor="gray.100" justifyContent="space-between" alignItems="center">
                  <Heading size="xs" color={crm.text}>Disponíveis</Heading>
                  <Text fontSize="xs" color={crm.textFaint}>{available.length}</Text>
                </Flex>
                <Box px="16px" py="8px" borderBottomWidth="1px" borderColor="gray.100" backgroundColor="gray.50">
                  <Input
                    size="xs" placeholder="Pesquisar coluna..." borderRadius="4px"
                    value={searchAvail} onChange={(e) => setSearchAvail(e.target.value)}
                    mb="6px" backgroundColor="white"
                  />
                  <Flex alignItems="center" gap="8px">
                    <Checkbox
                      isChecked={filteredAvailable.length > 0 && filteredAvailable.every((f) => checkedAvailable.includes(f))}
                      isIndeterminate={checkedAvailable.length > 0 && !filteredAvailable.every((f) => checkedAvailable.includes(f))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCheckedAvailable(Array.from(new Set([...checkedAvailable, ...filteredAvailable])));
                        } else {
                          setCheckedAvailable(checkedAvailable.filter((c) => !filteredAvailable.includes(c)));
                        }
                      }}
                      colorScheme="blue" />
                    <Text fontSize="xs" color="gray.500">Selecionar todos</Text>
                  </Flex>
                </Box>
                <AvailableFieldList fields={filteredAvailable} checked={checkedAvailable} onToggle={toggleAvail} />
              </Flex>

              <Flex flexDir="column" justifyContent="center" alignItems="center" gap="8px">
                <IconButton aria-label="Todos →" icon={<FaAngleDoubleRight />} size="sm" onClick={moveAllToSelected} isDisabled={available.length === 0} backgroundColor="var(--icon-color)" color="white" borderRadius="6px" />
                <IconButton aria-label="Marcados →" icon={<FaAngleRight />} size="sm" onClick={moveToSelected} isDisabled={checkedAvailable.length === 0} backgroundColor="var(--icon-color)" color="white" borderRadius="6px" />
                <IconButton aria-label="← Marcados" icon={<FaAngleLeft />} size="sm" onClick={moveToAvailable} isDisabled={checkedSelected.length === 0} backgroundColor="gray.200" color="gray.600" borderRadius="6px" />
                <IconButton aria-label="← Todos" icon={<FaAngleDoubleLeft />} size="sm" onClick={moveAllToAvailable} isDisabled={selected.length === 0} backgroundColor="gray.200" color="gray.600" borderRadius="6px" />
              </Flex>

              <Flex flex={7} flexDir="column" backgroundColor={crm.surface} borderRadius="8px" border={`1px solid ${crm.border}`} boxShadow={crm.shadowSm} overflow="hidden">
                <Flex px="16px" py="12px" borderBottomWidth="1px" borderColor="gray.100" justifyContent="space-between" alignItems="center">
                  <Heading size="xs" color={crm.text}>No relatório</Heading>
                  <Flex alignItems="center" gap="12px">
                    {activeFilterCount > 0 && (
                      <Button size="xs" variant="ghost" color="gray.400"
                        onClick={() => setFilters(emptyFilters())}>
                        Limpar filtros ({activeFilterCount})
                      </Button>
                    )}
                    <Text fontSize="xs" color={crm.textFaint}>{selected.length}</Text>
                  </Flex>
                </Flex>
                <Box px="16px" py="8px" borderBottomWidth="1px" borderColor="gray.100" backgroundColor="gray.50">
                  <Input
                    size="xs" placeholder="Pesquisar coluna..." borderRadius="4px"
                    value={searchSelected} onChange={(e) => setSearchSelected(e.target.value)}
                    mb="6px" backgroundColor="white"
                  />
                  <Flex alignItems="center" gap="8px">
                    <Checkbox
                      isChecked={filteredSelected.length > 0 && filteredSelected.every((f) => checkedSelected.includes(f))}
                      isIndeterminate={checkedSelected.length > 0 && !filteredSelected.every((f) => checkedSelected.includes(f))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCheckedSelected(Array.from(new Set([...checkedSelected, ...filteredSelected])));
                        } else {
                          setCheckedSelected(checkedSelected.filter((c) => !filteredSelected.includes(c)));
                        }
                      }}
                      colorScheme="green" />
                    <Text fontSize="xs" color="gray.500">Selecionar todos</Text>
                  </Flex>
                </Box>
                <SelectedFieldList
                  fields={filteredSelected} checked={checkedSelected} onToggle={toggleSel}
                  filters={filters} setFilters={setFilters}
                  companies={companies} loadingCompanies={loadingCompanies}
                  responsibles={responsibles} loadingResponsibles={loadingResponsibles}
                />
              </Flex>
            </Flex>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" borderColor="gray.100" gap="12px">
            <Button variant="ghost" color={crm.textMuted} onClick={handleClose} borderRadius="10px">
              Cancelar
            </Button>

            {selected.length > 0 && (
              <Text fontSize="sm" color={crm.textFaint}>
                {selected.length} coluna{selected.length !== 1 ? "s" : ""}
                {activeFilterCount > 0 && `, ${activeFilterCount} filtro${activeFilterCount !== 1 ? "s" : ""}`}
              </Text>
            )}

            <Button
              variant="outline" borderRadius="10px"
              isDisabled={selected.length === 0}
              leftIcon={<FaEye />}
              onClick={() => setIsPreviewOpen(true)}
            >
              Visualizar relatório
            </Button>
            <Button
              backgroundColor="var(--icon-color)" color="white" borderRadius="10px"
              _hover={{ backgroundColor: crm.brandDark }}
              isDisabled={selected.length === 0 || name.trim().length === 0}
              leftIcon={<FaFileExcel />}
              onClick={handleSave}
            >
              Gerar relatório
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ReportPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        selectedColumns={selected}
        filters={filters}
        name={name}
        getProcesses={getProcesses}
        currentCompany={currentCompany}
        currentEmulation={currentEmulation}
        onSave={() => {
          onSave?.(name, selected, filters);
          setIsPreviewOpen(false);
          handleClose();
        }}
      />
    </>
  );
};

export default NewReportModal;
